import {
    queryGraphQL,
    mutateGraphQL
} from 'common/util/query';
import {
    toQuery,
    toMutation,
    nodeWithArgs,
    node,
} from 'common/util/queryHelper';
import { addApplicationMessage } from 'common/actions/applicationMessagesActions';
import lodash from 'lodash';

import { loadBinderUsers } from 'binder/actions';
import { binderInstitutionIdSelector } from 'common/selectors';

export const USERS_CONFIG_LOAD_USERS = 'USERS_CONFIG_LOAD_USERS';
export const USERS_CONFIG_LOAD_PERMISSION_PROPERTIES = 'USERS_CONFIG_LOAD_PERMISSION_PROPERTIES';
export const USERS_CONFIG_CHANGE_SELECTION = 'USERS_CONFIG_CHANGE_SELECTION';
export const USERS_CONFIG_CHANGE_SEARCH = 'USERS_CONFIG_CHANGE_SEARCH';
export const USERS_CONFIG_TOGGLE_SETTING = 'USERS_CONFIG_TOGGLE_SETTING';
export const USERS_CONFIG_CHANGE_PERMISSIONS_DELTA = 'USERS_CONFIG_CHANGE_PERMISSION_DELTA';
export const USERS_CONFIG_GRANT_ACCESS = 'USERS_CONFIG_GRANT_ACCESS';
export const USERS_CONFIG_REVOKE_ACCESS = 'USERS_CONFIG_REVOKE_ACCESS';
export const USERS_CONFIG_INITIALIZE_PERMISSIONS_EDITOR = 'USERS_CONFIG_INITIALIZE_PERMISSIONS_EDITOR';
export const USERS_CONFIG_UNLOAD_USERS = 'USERS_CONFIG_UNLOAD_USERS';

export const ACTIONS = {
    USERS_CONFIG_LOAD_USERS,
    USERS_CONFIG_LOAD_PERMISSION_PROPERTIES,
    USERS_CONFIG_CHANGE_SELECTION,
    USERS_CONFIG_CHANGE_SEARCH,
    USERS_CONFIG_TOGGLE_SETTING,
    USERS_CONFIG_CHANGE_PERMISSIONS_DELTA,
    USERS_CONFIG_GRANT_ACCESS,
    USERS_CONFIG_REVOKE_ACCESS,
};

export function loadUsersConfiguration(binderId, extraArgs = {}) {
    return (dispatch, getState) => {
        const state = getState();
        const institutionId = binderInstitutionIdSelector(state);
        const includeSelf = lodash.get(extraArgs, 'includeSelf', false);

        const userQuery = nodeWithArgs('usersInSameGroups',{
            institutionId,
            binderId,
            includeSelf,
        },[
            'id',
            'sub',
            'displayName',
            'firstName',
            'lastName',
            'emailAddress',
            'isActive',
            nodeWithArgs('groups', {
                institutionId,
            }, [
                'id',
                'displayName',
                'description',
            ]),
            nodeWithArgs('binderPermissions', {
                binderId,
                institutionId
            }, [
                'name',
                'isRestricted',
                'value',
                'custom'
            ])
        ]);

        const permissionPropertiesQuery = node('permissionProperties', [
            'name',
            'displayName',
            'permissionPropertyType',
            node('allowedValues', [
                'allowedValue',
                'isDefault'
            ])
        ]);

        return queryGraphQL(toQuery([userQuery, permissionPropertiesQuery]))
            .then(response => {
                dispatch(writeUsers(response.data.usersInSameGroups));
                dispatch(writePermissionProperties(response.data.permissionProperties));
            });
    };
}

export function changeSelection(method, userSub) {
    return {
        type: USERS_CONFIG_CHANGE_SELECTION,
        payload: {
            sub: userSub,
            method: method || 'replace'
        }
    };
}

export function changeSearch(searchString) {
    return {
        type: USERS_CONFIG_CHANGE_SEARCH,
        payload: {
            searchString
        }
    };
}

// if value is not provided, the method will flip the truthiness of the existing value.
export function toggleUserPermissionsEditorSetting(setting, value) {
    return {
        type: USERS_CONFIG_TOGGLE_SETTING,
        payload: {
            setting,
            value
        }
    };
}

export function changePermissionsDelta(permissionName, value) {
    return {
        type: USERS_CONFIG_CHANGE_PERMISSIONS_DELTA,
        payload: {
            name: permissionName,
            value
        }
    };
}

export function grantAccess(permissionItems) {
    return {
        type: USERS_CONFIG_GRANT_ACCESS,
        payload: permissionItems
    };
}

export function revokeAccess() {
    return {
        type: USERS_CONFIG_REVOKE_ACCESS
    };
}

export function saveChanges(binderId, permissionItems) {
    return (dispatch, getState) => {
        dispatch(grantAccess(permissionItems));
        const state = getState();
        const { users, userPermissionsEditor } = state;
        const institutionId = binderInstitutionIdSelector(state);

        //maps to {sub: permissionsObject} pairs
        const updatedUsers = lodash.pickBy(users,
            u => userPermissionsEditor.updatedUsers.includes(u.sub));

        const permissions = lodash.mapValues(updatedUsers, user => {
            const { binderPermissions } = user;
            return lodash.mapValues(binderPermissions, bp => lodash.omit(bp, ['name']));
        });
        const updateUserBinderPermissions = nodeWithArgs('updateUserBinderPermissions', {
            userPermissions: {
                binderId,
                institutionId,
                permissions: JSON.stringify(permissions)
            }
        }, [
            'id',
            'sub',
            'displayName',
            'firstName',
            'lastName',
            'emailAddress',
            'institutionId',
            nodeWithArgs('groups', {
                institutionId,
            }, [
                'id',
                'displayName',
                'description',
            ]),
            nodeWithArgs('binderPermissions', {
                binderId,
                institutionId
            }, [
                'name',
                'isRestricted',
                'value',
                'custom'
            ])
        ]);

        dispatch(toggleUserPermissionsEditorSetting('isSaving', true));
        return mutateGraphQL(toMutation(updateUserBinderPermissions)).then(results => {
            if (results.errors) {
                dispatch(toggleUserPermissionsEditorSetting('isSaving', false));
                return Promise.reject(results.errors);
            }
            dispatch(loadBinderUsers(binderId, institutionId));
            dispatch(writeUsers(results.data.updateUserBinderPermissions));
            dispatch(initializeUserPermissionsEditor());
            dispatch(changeSelection('none'));
            dispatch(changeSearch(''));
            dispatch(toggleUserPermissionsEditorSetting('isSaving', false));
            dispatch(addApplicationMessage('success', 'User permissions saved successfully!'));
        });
    };
}

export function unloadUsers() {
    return dispatch => {
        dispatch({
            type: USERS_CONFIG_UNLOAD_USERS
        });
    };
}

export function initializeUserPermissionsEditor() {
    return {
        type: USERS_CONFIG_INITIALIZE_PERMISSIONS_EDITOR
    };
}

function writeUsers(users) {
    return {
        type: USERS_CONFIG_LOAD_USERS,
        payload: users
    };
}

function writePermissionProperties(permissionProperty) {
    return {
        type: USERS_CONFIG_LOAD_PERMISSION_PROPERTIES,
        payload: permissionProperty
    };
}





